/**
 * PATH: packages/email-hub/src/feature/email-layout/useLayoutPreferences.ts
 * FILE_ID: EMAIL-LAYOUT-PREFERENCES-001
 * PURPOSE: Own EmailLayout's persisted layout and surface-theme state.
 */

import { useEffect, useMemo, useRef, type CSSProperties } from 'react'
import {
  emailSurfaceCssVars,
  getEmailSurfaceTheme,
  hasEmailBackground,
} from '../../_internal/lib/emailSurfaceTheme'
import { useUiStore } from '../../_internal/stores/uiStore'

export function useLayoutPreferences(session: unknown) {
  // Workspace layout is two-tier: localStorage (instant, already loaded
  // synchronously by the store) + the DB (`EmailViewPreferences.
  // layout_json`, the durable source of truth). Hydrate from the server
  // ONCE per mount, after the session lands, so the operator's chosen
  // layout follows them across devices/browsers and survives a cache
  // clear — not just this browser. Fully fail-safe + server-wins-unless-
  // -touched; all the logic lives in the store.
  const hydrateLayoutFromServer = useUiStore(s => s.hydrateLayoutFromServer)
  const layoutHydratedRef = useRef(false)
  useEffect(() => {
    if (!session || layoutHydratedRef.current) return
    layoutHydratedRef.current = true
    void hydrateLayoutFromServer()
  }, [session, hydrateLayoutFromServer])

  // NOTE: `setSidebarCollapsed` used to live on this store back when
  // the email module also owned the OUTER app-shell sidebar flag.
  // The K3c lift moved outer-shell state out of the email-hub
  // _internal store, but the consumer reference here was left behind.
  // If a tenant wants outer-shell auto-collapse, wire it from that
  // tenant's AppShell, never from inside this package.
  const userCollapsed = useUiStore(s => s.emailSidebarCollapsed)
  const toggleCollapse = useUiStore(s => s.toggleEmailSidebar)
  const navPosition = useUiStore(s => s.navPosition)
  const background = useUiStore(s => s.background)
  const surfaceStyle = useMemo(() => {
    const theme = getEmailSurfaceTheme(background)
    return emailSurfaceCssVars(theme) as CSSProperties
  }, [background])
  const surfaceThemed = hasEmailBackground(background)

  return {
    userCollapsed,
    toggleCollapse,
    navPosition,
    surfaceStyle,
    surfaceThemed,
  }
}
